/**
 * @module app.cm
 */

import { autoinject, transient } from 'aurelia-framework';
import { Api } from 'app/shared/lib/api/api';
import { IQueryLoadOptions, IApiQueryResults, IApiSettingsResults, IApiActionResults, IApiBatchProgress } from 'app/shared/lib/api/api-types';
import { IMemberSearchFilters, countMemberFilters } from 'app/cd/services/member-service';
import { countHouseholdFilters, IHouseholdSearchFilters } from 'app/cd/services/household-service';
import { TaskWithLocalState } from 'app/core/models/local-state-extensions';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { createQueryResults } from 'app/shared/lib/api/api-utilities';
import { Toaster } from 'app/shared/lib/toaster';
import { IApiFilterResults } from 'app/core/models/api-results';
import { ApiQuery } from 'app/shared/lib/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as Core from 'app/core/models/essette-api';

export interface ICaseSearchFilters {
    acuity?: CM.Acuity | null;
    assignedTo?: Core.User | null;
    careTeamParticipant?: Core.User | null;
    caseNumber?: string | null;
    closedOnly?: boolean | null;
    createdDateEnd?: string | null;
    createdDateStart?: string | null;
    group?: CM.ProgramGroup | null;
    intensity?: CM.Intensity | null;
    mine?: boolean | null;
    myCareTeam?: boolean | null;
    openOnly?: boolean | null;
    program?: CM.Program | null;
    rollupModifiedDateEnd?: string | null;
    rollupModifiedDateStart?: string | null;
    status?: CM.CaseStatus | null;
}

/** Returns the number of defined filters. Useful for checking that X filters exist before running a search */
export function countCaseFilters(searchFilters: ICaseSearchFilters): number {
    if(!searchFilters) { return 0; }

    let count = 0;
    if(searchFilters.caseNumber) { count += 1; }
    if(searchFilters.status && searchFilters.status.id) { count += 1; }
    if(searchFilters.group && (searchFilters.group.id || searchFilters.group.id === '')) { count += 1; }
    if(searchFilters.program && searchFilters.program.id) { count += 1; }
    if(searchFilters.assignedTo && (searchFilters.assignedTo.id || searchFilters.assignedTo.id === '')) { count += 1; }
    if(searchFilters.careTeamParticipant && searchFilters.careTeamParticipant.id) { count += 1 }
    if(searchFilters.acuity && (searchFilters.acuity.id || searchFilters.acuity.id === '')) { count += 1; }
    if(searchFilters.intensity && (searchFilters.intensity.id || searchFilters.intensity.id === '')) { count += 1; }
    if(searchFilters.createdDateStart) { count += 1; } //End is assumed
    if(searchFilters.rollupModifiedDateStart) { count += 1; }
    if(!!searchFilters.mine) { count += 1; }
    if(!!searchFilters.myCareTeam) { count += 1; }
    if(!!searchFilters.openOnly) { count += 1; }
    if(!!searchFilters.closedOnly) { count += 1; }

    return count;
}

@transient()
@autoinject()
export class CaseService {

    ////****************** Functions ************************************************////
    constructor(
        private readonly api: Api,
        private readonly env: EnvironmentService,
        private readonly logger: AppLogger,
        private readonly toaster: Toaster) {

        this.logger.source = this;
    }

    public countCaseFilters(caseFilters: ICaseSearchFilters): number {
        return countCaseFilters(caseFilters);
    }
    public countMemberFilters(memberFilters: IMemberSearchFilters | null = null): number {
        return countMemberFilters(memberFilters);
    }

    public async get<T>(query: ApiQuery): Promise<IApiQueryResults<T>> {
        return await this.api.get(query) as IApiQueryResults<T>;
    }


    ////****************** Settings *************************************************////
    public async loadCaseSettings(includes: string[] = []): Promise<IApiSettingsResults> {
        return this.api.get(
            this.api.settings('cm/cases').include(...includes)
        );
    }


    ////****************** Queries **************************************************////
    public async searchCases(
        caseSearchFilters: ICaseSearchFilters,
        memberSearchFilters: IMemberSearchFilters,
        options?: Partial<IQueryLoadOptions>,
        includes: string[] = [],
        fields: string[] = []): Promise<IApiQueryResults<CM.Case>> {

        if(!caseSearchFilters) { caseSearchFilters = {}; }
        if(!memberSearchFilters) { memberSearchFilters = {}; }

        const caseFilterCount = countCaseFilters(caseSearchFilters);
        const memberFilterCount = countMemberFilters(memberSearchFilters);
        if(caseFilterCount + memberFilterCount === 0) {
            this.logger.warn('Invalid or missing search criteria');
            this.toaster.standard.searchNoCriteria();
            return createQueryResults('CM.Case', [], true);
        }

        const query = this.api
            .query('cm/cases/')
            .strategy('search')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(caseFilterCount > 0) {
            if(!!caseSearchFilters.mine && this.env.user.id) {
                if(caseSearchFilters.assignedTo && caseSearchFilters.assignedTo.id !== this.env.user.id) {
                    caseSearchFilters.assignedTo = <Core.User>this.env.user;
                    caseSearchFilters.assignedTo.$display = this.env.user.description;
                }
            }
            if(!!caseSearchFilters.myCareTeam && this.env.user.id) {
                if(caseSearchFilters.careTeamParticipant && caseSearchFilters.careTeamParticipant.id !== this.env.user.id) {
                    caseSearchFilters.careTeamParticipant = <Core.User>this.env.user;
                    caseSearchFilters.careTeamParticipant.$display = this.env.user.description;
                }
            }
            const joinMyCaseFilters = !!caseSearchFilters.mine && !!caseSearchFilters.myCareTeam;
            if(joinMyCaseFilters) {
                // If both 'My Cases' and 'My Care Team Cases' is selected, treat as an OR.
                query.filterOr((or) => {
                    or.filterIdentifier('assignedTo.id', caseSearchFilters.assignedTo?.id!);
                    or.filterExists('careteam.participants', (f =>
                        f.filterString('assigned.toUser.id', caseSearchFilters.careTeamParticipant?.id!, 'eq')
                    ));
                });
            }
            if(!joinMyCaseFilters && caseSearchFilters.assignedTo && caseSearchFilters.assignedTo.id && !caseSearchFilters.careTeamParticipant) {
                // Otherwise, if only case owner is specified, then override url
                query.overrideUrl(`cm/cases/owners/${caseSearchFilters.assignedTo.id}/cases`);
            } else if(!joinMyCaseFilters && caseSearchFilters.assignedTo && caseSearchFilters.assignedTo.id) {
                // Filter by assigned to (can be null)
                query.filterIdentifier('assignedTo.id', caseSearchFilters.assignedTo.id);
            } else if(!joinMyCaseFilters && caseSearchFilters.assignedTo && caseSearchFilters.assignedTo.id === '') {
                query.filterIsNull('assignedTo.id')
            }
            if(!joinMyCaseFilters && caseSearchFilters.careTeamParticipant && caseSearchFilters.careTeamParticipant.id) {
                // Then filter by care team member;
                query.filterExists('careteam.participants', (f =>
                    f.filterString('assigned.toUser.id', caseSearchFilters.careTeamParticipant?.id!, 'eq')
                ));
            }

            if(!!caseSearchFilters.openOnly) {
                query.filterBoolean('isClosed', false);
            }
            else if(!!caseSearchFilters.closedOnly) {
                query.filterBoolean('isClosed', true);
            }

            if(caseSearchFilters.caseNumber) {
                query.filterString('number', caseSearchFilters.caseNumber, 'startsWith');
            }
            if(caseSearchFilters.status && caseSearchFilters.status.id) {
                query.filterIdentifier('status.id', caseSearchFilters.status.id);
            }
            if(caseSearchFilters.group) {
                query.filterString('program.group.id', caseSearchFilters.group.id || null);
            }
            if(caseSearchFilters.program && caseSearchFilters.program.id) {
                query.filterIdentifier('program.id', caseSearchFilters.program.id);
            }
            if(caseSearchFilters.createdDateStart) {
                query.filterDateTimeBetweenDates('metadata.created.timestamp', caseSearchFilters.createdDateStart, caseSearchFilters.createdDateEnd);
            }
            if(caseSearchFilters.rollupModifiedDateStart) {
                query.filterDateTimeBetweenDates('metadata.rollupModified.timestamp', caseSearchFilters.rollupModifiedDateStart, caseSearchFilters.rollupModifiedDateEnd);
            }
            if(caseSearchFilters.acuity) {
                query.filterString('acuity.id', caseSearchFilters.acuity.id || null);
            }
            if(caseSearchFilters.intensity) {
                query.filterString('intensity.id', caseSearchFilters.intensity.id || null);
            }
        }

        if(memberFilterCount > 0) {
            if(memberSearchFilters.id) {
                query.filterIdentifier('member.id', memberSearchFilters.id);
            }
            if(memberSearchFilters.memberId) {
                query.filterString('member.identifier.primary', memberSearchFilters.memberId, 'startsWith');
            }
            if(memberSearchFilters.medicaidId) {
                query.filterString('member.identifier.medicaid', memberSearchFilters.medicaidId, 'startsWith');
            }
            if(memberSearchFilters.memberFirstName) {
                query.filterString('member.name.first', memberSearchFilters.memberFirstName, 'startsWith');
            }
            if(memberSearchFilters.memberLastName) {
                query.filterString('member.name.last', memberSearchFilters.memberLastName, 'startsWith');
            }
            if(memberSearchFilters.dob) {
                query.filterDate('member.birthDate', memberSearchFilters.dob);
            }
            if(!!memberSearchFilters.eligibility) {
                query.filterBoolean('member.eligibility.isCurrent', true);
            }
            if(memberSearchFilters.healthPlan && memberSearchFilters.healthPlan.id) {
                query.filterIdentifier('member.primaryPlan.healthPlan.id', memberSearchFilters.healthPlan.id);
            }
            if(memberSearchFilters.lineOfBusiness && memberSearchFilters.lineOfBusiness.id) {
                query.filterIdentifier('member.primaryPlan.lineOfBusiness.id', memberSearchFilters.lineOfBusiness.id);
            }
            if(memberSearchFilters.pcpId) {
                query.filterString('member.primaryCarePhysician.identifier.primary', memberSearchFilters.pcpId, 'startsWith');
            }
            if(memberSearchFilters.pcpFirstName) {
                query.filterString('member.primaryCarePhysician.name.first', memberSearchFilters.pcpFirstName, 'startsWith');
            }
            if(memberSearchFilters.pcpLastName) {
                query.filterString('member.primaryCarePhysician.name.last', memberSearchFilters.pcpLastName, 'startsWith');
            }
            if(memberSearchFilters.householdName) {
                query.filterString('householdCase.household.description', memberSearchFilters.householdName!, 'startsWith');
            }
            if(memberSearchFilters.householdId) {
                query.filterString('householdCase.household.identifier.primary', memberSearchFilters.householdId!, 'startsWith');
            }
        }


        return await this.api.get(query);
    }

    public async loadCasesById(
        caseIds: string[],
        options?: Partial<IQueryLoadOptions>,
        includes: string[] = [],
        fields: string[] = []
    ): Promise<IApiQueryResults<CM.Case>> {
        const query = this.api
            .query(`cm/cases`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields)
            .filterIdentifierArray('id', caseIds, 'eq');

        return await this.api.get(query);
    }

    public async loadCasesByMember(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.Case>> {
        const query = this.api
            .query(`cm/members/${memberId}/cases`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadCasesByHouseholdCase(householdCaseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.Case>> {
        const query = this.api
            .query(`cm/households/cases/${householdCaseId}/cases`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadCaseCareTeam(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CareTeamParticipant>> {
        const query = this.api
            .query(`cm/cases/${caseId}/careTeam/participants`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadHouseholdCaseCareTeam(householdCaseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CareTeamParticipant>> {
        const query = this.api
            .query(`cm/households/cases/${householdCaseId}/careTeam/participants`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadCasesCreatedByTask(taskId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.Case>> {
        const query = this.api
            .query('cm/cases')
            .applyStandardLoadOptions(options || null)
            .include(...includes)
            .filterIdentifier('metadata.created.byTask.id', taskId);

        return await this.api.get(query);
    }

    public async loadCriteria(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.ReferralType>> {
        const query = this.api
            .query('cm/referrals/types')
            .applyStandardLoadOptions(options || null)
            .include(...includes)
            .filterExists("criteria");

        return await this.api.get(query);
    }

    public async loadAssignmentHistoryByCase(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CaseAssignmentHistory>> {
        const query = this.api
            .query(`cm/cases/${caseId}/history/assignments`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAssignmentHistoryByHouseholdCase(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CaseAssignmentHistory>> {
        const query = this.api
            .query(`cm/households/cases/${caseId}/history/assignments`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadQueues(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CaseQueue>> {
        const query = this.api
            .query('cm/cases/queues')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public createLoadCasesByQueueQuery(queueId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): ApiQuery {
        const query = this.api
            .query(`cm/cases/queues/${queueId}/cases`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return query;
    }
    public async loadCasesByQueue(queueId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.Case>> {
        const query = this.createLoadCasesByQueueQuery(queueId, options, includes, fields);

        return await this.api.get(query);
    }

    public createLoadQueueQuery(queueId: string, includes: string[] = []): ApiQuery {
        const query = this.api
            .query(`cm/cases/queues/${queueId}`)
            .include(...includes);

        return query;
    }
    public async loadQueue(queueId: string, includes: string[] = []): Promise<IApiQueryResults<CM.CaseQueue>> {
        const query = this.createLoadQueueQuery(queueId, includes);

        return await this.api.get(query);
    }

    public async loadTaskQueues(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.TaskQueue>> {
        const query = this.api
            .query('cm/tasks/queues')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadQueueHistoryByCase(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CaseQueueHistory>> {
        const query = this.api
            .query(`cm/cases/${caseId}/history/queues`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadQueueHistoryByTask(taskId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.TaskQueueHistory>> {
        const query = this.api
            .query(`cm/tasks/${taskId}/history/queues`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    /** Loads followup interventions for a given intervention definition  */
    public async loadInterventionFollowupsByIntervention(interventionDefinitionId: string, isOptional: boolean, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CarePlanInterventionDefinitionWithFollowupDetailModel>> {
        const query = this.api
            .query(`cm/carePlans/interventions/definitions/${interventionDefinitionId}/followups`)
            .filterBoolean('detail.isOptional', isOptional)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    /** Loads followup tasks for a given task definition  */
    public async loadTaskFollowupsByTask(taskDefinitionId: string, isOptional: boolean, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CaseTaskDefinitionWithFollowupDetailModel>> {
        const query = this.api
            .query(`cm/cases/tasks/definitions/${taskDefinitionId}/followups`)
            .filterBoolean('detail.isOptional', isOptional)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }


    /**
     * Load all `CM.ReferralSource` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadReferralSources(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.ReferralSource>> {
        const query = this.api
            .query('cm/referrals/sources')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async activateReferralSource(id: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `/CM/Referrals/Sources/${id}/`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);

        } else {
            return await this.api.delete(action);
        }
    }

    public async saveReferralSource(referralSource: CM.ReferralSource): Promise<IApiActionResults> {
        const actionName = !referralSource.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `CM/Referrals/Sources/${actionName === 'update' ? referralSource.id : ''}`, referralSource);

        return await this.api.post(action);
    }

    /**
     * Load all `CM.ReferralDestination` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadReferralDestinations(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.ReferralDestination>> {
        const query = this.api
            .query('cm/referrals/destinations')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async activateReferralDestination(id: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `/cm/referrals/destinations/${id}/`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async saveReferralDestination(referralDestination: CM.ReferralDestination): Promise<IApiActionResults> {
        const actionName = !referralDestination.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cm/referrals/destinations/${actionName === 'update' ? referralDestination.id : ''}`, referralDestination);

        return await this.api.post(action);
    }

    /**
     * Load all `CM.ReferralType` models
     * @param options query load options
     * @param includes related models to include
     * @param fields related fields to include
     */
    public async loadReferralTypes(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.ReferralType>> {
        const query = this.api
            .query('cm/referrals/types')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    /**
     * Load all `CM.ReferralType` models
     * @param options query load options
     * @param includes related models to include
     * @param fields related fields to include
     */
    public async loadReferralTypeCriteria(referralTypeId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.ReferralType>> {
        const query = this.api
            .query(`cm/referrals/types/${referralTypeId}/criteria`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async saveReferralType(referralType: CM.ReferralType): Promise<IApiActionResults> {
        const actionName = !referralType.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cm/referrals/types/${actionName === 'update' ? referralType.id : ''}`, referralType);

        return await this.api.post(action);
    }

    public async activateReferralType(id: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `/cm/referrals/types/${id}/`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async sortReferralTypes(referralTypeIDs: string[]): Promise<IApiActionResults> {
        const action = this.api.action('sort', 'cm/referrals/types/', { $sort: referralTypeIDs });

        return await this.api.post(action);
    }

    public async saveCriteria(criteria: CM.ReferralCriteria): Promise<IApiActionResults> {
        const actionName = !criteria.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cm/referrals/criteria/${actionName === 'update' ? criteria.id : ''}`, criteria);

        return await this.api.post(action);
    }

    public async activateCriteria(id: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `/cm/referrals/criteria/${id}/`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async sortCriteria(referralType: CM.ReferralType, criteriaIDs: string[]): Promise<IApiActionResults> {
        const action = this.api.action('sort', 'cm/referrals/criteria/', { type: referralType, $sort: criteriaIDs });

        return await this.api.post(action);
    }

    /**
     * Load all `CM.ReferralReason` models
     * @param options query load options
     * @param includes related models to include in the query results
     */
    public async loadReferralReasons(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.ReferralReason>> {
        const query = this.api
            .query('cm/referrals/reasons')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async activateReferralReason(id: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `/CM/Referrals/Reasons/${id}/`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);

        } else {
            return await this.api.delete(action);
        }
    }

    public async saveReferralReason(referralReason: CM.ReferralReason): Promise<IApiActionResults> {
        const actionName = !referralReason.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cm/referrals/reasons/${actionName === 'update' ? referralReason.id : ''}`, referralReason);

        return await this.api.post(action);
    }

    public createLoadCaseOwnersQuery(options?: Partial<IQueryLoadOptions>, includes: string[] = [], excludeEmpty: boolean = false, feature: string | null = null, fields: string[] = []): ApiQuery {
        const query = this.api
            .query('cm/cases/owners')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(excludeEmpty) {
            query.filterDirect('openCaseCount gt 0');
        }

        if(feature) {
            query.feature(feature);
        }

        return query;
    }
    /** Loads users who have the case owner permission
     * @param excludeEmpty true: only return case owners that have at least one open case
     */
    public async loadCaseOwners(options?: Partial<IQueryLoadOptions>, includes: string[] = [], excludeEmpty: boolean = false, feature: string | null = null, fields: string[] = []): Promise<IApiQueryResults<CM.CaseOwner>> {
        const query = this.createLoadCaseOwnersQuery(options, includes, excludeEmpty, feature, fields);

        return await this.api.get(query);
    }

    /**
     * Load all `CM.Acuity` models
     * @param options query load options
     * @param includes related models to include in the query results
     */
    public async loadAcuities(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.Acuity>> {
        const query = this.api
            .query('cm/acuities')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async activateAcuity(id: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `/CM/Acuities/${id}/`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);

        } else {
            return await this.api.delete(action);
        }
    }

    public async saveAcuity(acuity: CM.Acuity): Promise<IApiActionResults> {
        const actionName = !acuity.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cm/acuities/${actionName === 'update' ? acuity.id : ''}`, acuity);

        return await this.api.post(action);
    }


    /**
     * Load all `CM.Intensity` models
     * @param options query load options
     * @param includes related models to include in the query results
     */
    public async loadIntensities(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.Intensity>> {
        const query = this.api
            .query('cm/intensities')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async activateIntensity(id: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `/CM/Intensities/${id}/`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);

        } else {
            return await this.api.delete(action);
        }
    }

    public async saveIntensity(intensity: CM.Intensity): Promise<IApiActionResults> {
        const actionName = !intensity.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cm/intensities/${actionName === 'update' ? intensity.id : ''}`, intensity);

        return await this.api.post(action);
    }

    /**
     * Load all `CM.TaskTargetType` models
     * @param options query load options
     * @param includes related models to include in the query results
     * @param fields targeted fields to query
     */
    public async loadTaskTargetTypes(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.TaskTargetType>> {
        const query = this.api
            .query('cm/tasks/targets/types')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadCaseServices(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CaseService>> {
        const query = this.api
            .query(`cm/cases/${caseId}/services`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadHouseholdCaseServices(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CaseService>> {
        const query = this.api
            .query(`cm/households/cases/${caseId}/services`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async activateCaseService(serviceId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `CM/Cases/Services/${serviceId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async loadCaseStatuses(
        options?: Partial<IQueryLoadOptions>,
        includes: string[] = [],
        flag?: CM.CaseStatusFlags,
        fields: string[] = []): Promise<IApiQueryResults<CM.CaseStatus>> {

        const query = this.api
            .query('cm/cases/statuses')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(flag) {
            if(flag.isInitial !== undefined && flag.isInitial !== null) { query.filterBoolean('flag.isInitial', flag.isInitial); }
            if(flag.isOpen !== undefined && flag.isOpen !== null) { query.filterBoolean('flag.isOpen', flag.isOpen); }
            if(flag.isDeclined !== undefined && flag.isDeclined !== null) { query.filterBoolean('flag.isDeclined', flag.isDeclined); }
            if(flag.addsCaseNote !== undefined && flag.addsCaseNote !== null) { query.filterBoolean('flag.addsCaseNote', flag.addsCaseNote); }
        }

        return await this.api.get(query);
    }

    public async loadHouseholdCaseStatuses(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.HouseholdCaseStatus>> {
        const query = this.api.query('cm/households/cases/statuses')
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadStatusReasonsByStatus(statusId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CaseStatusReason>> {
        const query = this.api
            .query(`cm/cases/statuses/${statusId}/reasons`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadContactLogByCase(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CaseContactLog>> {
        const query = this.api
            .query(`CM/Cases/${caseId}/ContactLog`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadCareTeamParticipantHistory(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CareTeamParticipantHistory>> {
        const query = this.api
            .query(`cm/careteams/history/participants`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .filterIdentifier('case.id', caseId);

        return await this.api.get(query);
    }

    public async loadCaseStatusHistory(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CaseStatusHistory>> {
        const query = this.api
            .query(`cm/cases/${caseId}/History/Statuses`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadCaseTaskHistory(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], interventionsOnly: boolean = false): Promise<IApiQueryResults<CM.TaskHistory>> {
        const query = this.api
            .query(`cm/cases/${caseId}/History/Tasks`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        if(interventionsOnly) {
            query.filterNot(not => not.filterIsNull('goal.id'));
        } else {
            query.filterIsNull('goal.id');
        }

        return await this.api.get(query);
    }

    public async loadTaskQueueHistoryByCaseId(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.TaskQueueHistory>> {
        const query = this.api
            .query(`cm/tasks/history/queues`)
            .applyStandardLoadOptions(options)
            .filterIsNull('goal.id')
            .filterIdentifier('case.id', caseId)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadHouseholdCaseStatusHistory(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.HouseholdCaseStatusHistory>> {
        const query = this.api
            .query(`cm/households/cases/${caseId}/history/statuses`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadCaseProgramHistory(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CaseProgramHistory>> {
        const query = this.api
            .query(`cm/cases/${caseId}/history/programs`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadCaseAcuityHistory(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CaseAcuityHistory>> {
        const query = this.api
            .query(`cm/cases/${caseId}/history/acuities`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadCaseIntensityHistory(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CaseIntensityHistory>> {
        const query = this.api
            .query(`cm/cases/${caseId}/history/intensities`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadCaseCheckoutHistory(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CaseCheckoutHistory>> {
        const query = this.api
            .query(`cm/cases/${caseId}/history/checkouts`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(!options?.includeInactive) {
            query.filterIsNull('metadata.ended.timestamp')
        }

        return await this.api.get(query);
    }


    public async loadCaseServiceHistory(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CaseServiceHistory>> {
        const query = this.api
            .query(`cm/cases/history/services`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .filterIdentifier('caseService.case.id', caseId);

        return await this.api.get(query);
    }

    public async loadPrograms(options?: Partial<IQueryLoadOptions>, includes: string[] = [], includeSystem: boolean = false): Promise<IApiQueryResults<CM.Program>> {
        const query = this.api
            .query(`cm/programs`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        if(!includeSystem) {
            query.filterBoolean('isSystem', false)
        }

        return await this.api.get(query);
    }

    public async loadProgramGroups(options?: Partial<IQueryLoadOptions>, includes: string[] = [], includeSystem: boolean = false, includeInactivePrograms: boolean = false, excludeWithoutPrograms: boolean = false, fields: string[] = []): Promise<IApiQueryResults<CM.ProgramGroup>> {
        const query = this.api
            .query('cm/programs/groups')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(includeInactivePrograms) query.includeWithState('inactive', 'programs');
        if(excludeWithoutPrograms) query.filterExists('programs');
        if(!includeSystem) query.filterBoolean('isSystem', false)

        return await this.api.get(query);
    }

    public async loadReferralOrigins(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.ReferralOrigin>> {
        const query = this.api
            .query(`cm/referrals/origins`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async searchHouseholdCases(householdSearchFilters: IHouseholdSearchFilters, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.HouseholdCase>> {
        if(!householdSearchFilters) { householdSearchFilters = {}; }

        const householdFilterCount = countHouseholdFilters(householdSearchFilters);

        if(householdFilterCount === 0) {
            this.logger.warn('Invalid or missing search criteria');
            this.toaster.standard.searchNoCriteria();
            return createQueryResults('CM.HouseholdCase', [], true);
        }

        const query = this.api
            .query(`cm/households/cases/`)
            .strategy('search')
            .applyStandardLoadOptions(options)
            .include(...includes);

        // Searching for Cases based on parent Household information. Search filter based on equals.
        if(householdFilterCount > 0) {
            if(householdSearchFilters.householdId) {
                query.filterIdentifier('household.id', householdSearchFilters.householdId);
            }
        }

        return await this.api.get(query);
    }

    ////****************** Loads ****************************************************////
    public async loadCase(caseId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.Case>> {
        const query = this.api
            .query(`cm/cases/${caseId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadCaseByDetail(detailProperty: string, detailId: string, includes: string[] = []): Promise<IApiQueryResults<CM.Case>> {
        const query = this.api
            .query(`cm/cases/`).filterExists(detailProperty, q => q.filterIdentifier('id', detailId)).top(1)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadCarePlanGoal(goalId: string, includes: string[] = []): Promise<IApiQueryResults<CM.CarePlanGoal>> {
        const query = this.api
            .query(`cm/carePlans/goals/${goalId}`)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadCarePlanGoalHistoryByCase(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CarePlanGoalHistory>> {
        const query = this.api
            .query(`cm/careplans/history/goals`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .filterIdentifier('case.id', caseId);

        return await this.api.get(query);
    }

    public async loadCarePlanBarriersByCase(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CarePlanBarrier>> {
        const query = this.api
            .query(`cm/careplans/barriers`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .filterIdentifier('case.id', caseId);

        return await this.api.get(query);
    }

    public async loadCarePlanOutcomesByCase(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CarePlanOutcome>> {
        const query = this.api
            .query(`cm/careplans/outcomes`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .filterIdentifier('case.id', caseId);

        return await this.api.get(query);
    }

    public async loadCaseService(caseId: string, taskId: string, includes: string[] = []): Promise<IApiQueryResults<CM.CaseService>> {
        const query = this.api
            .query(`cm/cases/services`)
            .filterIdentifier('task.id', taskId)
            .filterIdentifier('case.id', caseId)
            .includeInactive(true)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadCaseCareTeamComments(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CareTeamComment>> {
        const query = this.api
            .query(`cm/cases/${caseId}/careteam/comments`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadHouseholdCase(caseId: string, includes: string[] = []): Promise<IApiQueryResults<CM.HouseholdCase>> {
        const query = this.api
            .query(`cm/households/cases/${caseId}`)
            .include(...includes)
            .top(1);

        return await this.api.get(query);
    }

    public async loadProgram(programId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], includeSystem: boolean = false): Promise<IApiQueryResults<CM.Program>> {
        const query = this.api
            .query(`cm/programs/${programId}`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        if(!includeSystem) {
            query.filterBoolean('isSystem', false)
        }

        return await this.api.get(query);
    }

    ////****************** Actions **************************************************////
    public async routeToQueue($case: CM.Case): Promise<IApiActionResults> {
        const action = this.api.action('route', `cm/cases/${$case.id}`, $case);

        return await this.api.post(action);
    }

    public async returnFromQueue(caseId: string, queueId: string): Promise<IApiActionResults> {
        const $case: CM.Case = {
            id: caseId,
            queues: [{ id: queueId }]
        }

        const action = this.api.action('completeRoute', `cm/cases/${$case.id}`, $case);
        return await this.api.post(action);
    }

    public async insertReferralVerification(referralVerification: CM.ReferralVerification): Promise<IApiActionResults> {
        const action = this.api.action('insert', 'cm/referrals/verifications', referralVerification);

        return await this.api.post(action);
    }

    public async insertCase($case: CM.Case): Promise<IApiActionResults> {
        const action = this.api.action('insert', 'cm/cases', $case);

        return await this.api.post(action);
    }

    public async initializeHouseholdCase(householdCase: CM.HouseholdCase): Promise<IApiActionResults> {
        const action = this.api.action('initialize', `cm/households/cases/${householdCase.id}`, householdCase);

        return await this.api.post(action);
    }

    public async updateCase($case: CM.Case): Promise<IApiActionResults> {
        const action = this.api.action('update', `cm/cases/${$case.id}`, $case);

        return await this.api.post(action);
    }

    public async updateBatchCases(cases: CM.Case[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        const batch = this.api.batch();
        for(const c of cases) {
            batch.post(this.api.action('update', `cm/cases/${c.id}`, c));
        }
        return await this.api.execute(batch, { progress });
    }

    public async bulkUpdateCase($case: CM.Case): Promise<IApiActionResults> {
        const action = this.api.action('bulkUpdate', `cm/cases`, $case);

        return await this.api.post(action);
    }

    public async assignCase($case: CM.Case): Promise<IApiActionResults> {
        const action = this.api.action('reassign', `cm/cases/${$case.id}`, $case);

        return await this.api.post(action);
    }

    public async assignHouseholdCase($case: CM.HouseholdCase): Promise<IApiActionResults> {
        const action = this.api.action('reassign', `cm/households/cases/${$case.id}`, $case);

        return await this.api.post(action);
    }

    public async updateHouseholdCaseStatus($case: CM.HouseholdCase): Promise<IApiActionResults> {
        const action = this.api.action('updatestatus', `cm/households/cases/${$case.id}`, $case)

        return await this.api.post(action);
    }

    /**
     * Assign multiple cases in a single batch
     * @param cases array of case assignments
     * @returns array of results
     */
    public async assignCasesBatch(cases: CM.Case[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        const batch = this.api.batch();
        for(const c of cases) {
            batch.post(this.api.action('reassign', `cm/cases/${c.id}`, c))
        }
        return await this.api.execute(batch, { progress });
    }

    /**
     * Apply multiple tasks to multiple cases
     * @param cases array of cases
     * @param tasks array of tasks
     * @returns array of results
     */
    public async insertTasksToCasesBatch(tasks: TaskWithLocalState[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        const batch = this.api.batch();
        for(const t of tasks) {
            batch.post(this.api.action('insert', 'cm/cases/tasks', t));
        }
        return await this.api.execute(batch, { progress });
    }

    public async insertHouseholdCase(householdCase: CM.HouseholdCase): Promise<IApiActionResults> {
        const action = this.api.action('insert', 'cm/households/cases', householdCase);

        return await this.api.post(action);
    }

    public async verifyAssignment(userId: string, cases: CM.Case[]): Promise<IApiFilterResults> {
        const body = { filter: { model: 'cm.case', items: cases } }
        const action = this.api.action('filter', `users/${userId}`, body);

        return await this.api.post(action);
    }

    public async cancelCheckout(checkout: CM.CaseCheckoutHistory): Promise<IApiActionResults> {
        const action = this.api.action('deactivate', `cm/cases/history/checkouts/${checkout.id}`, checkout);

        return await this.api.delete(action);

    }

    public async insertService($service: CM.CaseService): Promise<IApiActionResults> {
        const action = this.api.action('insert', `cm/cases/services`, $service);

        return await this.api.post(action);
    }

    public async insertServiceBatch(services: CM.CaseService[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        const batch = this.api.batch();

        services.forEach(service => batch.post(this.api.action('insert', `cm/cases/services`, service))
        );

        return await this.api.execute(batch, { progress });
    }

    public async updateService($service: CM.CaseService, reason: string): Promise<IApiActionResults> {
        const action = this.api
            .action('update', `cm/cases/services/${$service.id}`, $service)
            .withReason(reason);

        return await this.api.post(action);
    }

    public async insertCareTeamParticipant($careTeamParticipant: CM.CareTeamParticipant): Promise<IApiActionResults> {
        const action = this.api.action('insert', `cm/careteams/participants`, $careTeamParticipant);

        return await this.api.post(action);
    }

    public async activateCareTeamParticipant(careTeamParticipantId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cm/careteams/participants/${careTeamParticipantId}`, { isActive: isActive });

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async addCareTeamComment(comment: CM.CareTeamComment): Promise<IApiActionResults> {
        const action = this.api.action('insert', 'cm/careteams/comments', comment);

        return await this.api.post(action);
    }

    public async activateQueue(queueId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cm/cases/queues/${queueId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async insertQueue(queue: CM.CaseQueue): Promise<IApiActionResults> {
        const actionName = !queue.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cm/cases/queues/${actionName === 'update' ? queue.id : ''}`, queue);

        return await this.api.post(action);
    }
}

